export type DisplayTimezonePreference =
  | { mode: 'automatic' }
  | { mode: 'manual'; timeZone: string };

export const DEFAULT_DISPLAY_TIMEZONE_PREFERENCE: DisplayTimezonePreference = { mode: 'automatic' };

export function isValidIanaTimezone(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: value.trim() }).format(0); return true; }
  catch { return false; }
}

export function normalizeDisplayTimezonePreference(value: unknown): DisplayTimezonePreference {
  if (value && typeof value === 'object' && (value as any).mode === 'manual' && isValidIanaTimezone((value as any).timeZone)) {
    return { mode: 'manual', timeZone: String((value as any).timeZone).trim() };
  }
  return DEFAULT_DISPLAY_TIMEZONE_PREFERENCE;
}

export function resolveDisplayTimezone(preference: DisplayTimezonePreference, browserZone?: string): string {
  if (preference.mode === 'manual' && isValidIanaTimezone(preference.timeZone)) return preference.timeZone;
  if (isValidIanaTimezone(browserZone)) return browserZone;
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidIanaTimezone(detected) ? detected : 'UTC';
  } catch { return 'UTC'; }
}

export function formatDateTimeInTimezone(timestampMs: number, timeZone: string): { dateStr: string; timeStr: string } {
  if (!Number.isFinite(timestampMs) || !isValidIanaTimezone(timeZone)) return { dateStr: '', timeStr: '' };
  const date = new Date(timestampMs);
  const dateStr = new Intl.DateTimeFormat('en-US', { day: '2-digit', month: 'short', year: 'numeric', timeZone }).format(date);
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone,
  }).formatToParts(date).map((part) => [part.type, part.value]));
  return { dateStr, timeStr: `${String(parts.hour || '00').padStart(2, '0')}:${String(parts.minute || '00').padStart(2, '0')}:${String(parts.second || '00').padStart(2, '0')}` };
}

export function formatDateTimeInputInTimezone(timestampMs: number, timeZone: string): string {
  if (!Number.isFinite(timestampMs) || !isValidIanaTimezone(timeZone)) return '';
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(timestampMs)).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}
